import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  loadPrpWorkspace,
  runPrpForfy,
  type PrpForfyMode,
  type PrpWorkspaceReadModel,
} from "../../lib/prpWorkspace";

type TargetOption = {
  key: string;
  type:
    | "overview"
    | "lead"
    | "business";
  id: string | null;
  label: string;
};

const modeLabels:
  Array<{
    value: PrpForfyMode;
    fr: string;
    en: string;
  }> = [
    {
      value:
        "next_best_action",
      fr: "Prochaine meilleure action",
      en: "Next best action",
    },
    {
      value:
        "prepare_outreach",
      fr: "Préparer une approche",
      en: "Prepare outreach",
    },
    {
      value:
        "meeting_brief",
      fr: "Préparer une rencontre",
      en: "Meeting brief",
    },
    {
      value:
        "follow_up_draft",
      fr: "Brouillon de suivi",
      en: "Follow-up draft",
    },
    {
      value:
        "pipeline_risk",
      fr: "Analyser le risque pipeline",
      en: "Pipeline risk",
    },
  ];

function number(
  value: unknown,
) {
  const parsed =
    Number(value || 0);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
}

const PrpGrowthCockpit:
  React.FC = () => {
    const [open, setOpen] =
      useState(false);

    const [loading, setLoading] =
      useState(false);

    const [running, setRunning] =
      useState(false);

    const [data, setData] =
      useState<
        PrpWorkspaceReadModel | null
      >(null);

    const [error, setError] =
      useState<string | null>(
        null,
      );

    const [result, setResult] =
      useState<string | null>(
        null,
      );

    const [mode, setMode] =
      useState<PrpForfyMode>(
        "next_best_action",
      );

    const [
      selectedTarget,
      setSelectedTarget,
    ] =
      useState(
        "overview",
      );

    const [
      objective,
      setObjective,
    ] =
      useState("");

    const language:
      "fr" | "en" =
      typeof navigator
        !== "undefined"
        && navigator.language
          .toLowerCase()
          .startsWith("en")
        ? "en"
        : "fr";

    const text =
      language === "en"
        ? {
            button:
              "IA Forfy PRP",
            title:
              "PRP Growth Cockpit",
            subtitle:
              "Server-resolved portfolio, territory and safe Forfy PRP copilot.",
            refresh:
              "Refresh",
            leads:
              "Prospects",
            merchants:
              "Merchants",
            active:
              "Approved",
            payouts:
              "Pending payouts",
            target:
              "Target",
            mode:
              "IA Forfy mode",
            objective:
              "Objective / context",
            objectivePlaceholder:
              "Example: prepare my client meeting, objections, angle and next action.",
            run:
              "Ask Forfy PRP",
            close:
              "Close",
            noData:
              "No data available.",
            human:
              "IA Forfy recommends and drafts. You remain responsible for every external action.",
            financial:
              "Official commissions remain resolved by Giftshop. No financial calculation is performed here.",
            gateway:
              "Forfy PRP is active in safe coaching mode. No financial action can be executed here.",
            workspace:
              "The PRP cockpit could not load. Refresh your session and try again.",
          }
        : {
            button:
              "IA Forfy PRP",
            title:
              "Cockpit croissance PRP",
            subtitle:
              "Portefeuille, territoire et copilote Forfy PRP sécurisé.",
            refresh:
              "Actualiser",
            leads:
              "Prospects",
            merchants:
              "Marchands",
            active:
              "Approuvés",
            payouts:
              "Retraits en attente",
            target:
              "Cible",
            mode:
              "Mode IA Forfy",
            objective:
              "Objectif / contexte",
            objectivePlaceholder:
              "Ex. Prépare ma rencontre client, objections, angle et prochaine action.",
            run:
              "Demander à Forfy PRP",
            close:
              "Fermer",
            noData:
              "Aucune donnée disponible.",
            human:
              "IA Forfy recommande et rédige. Toute action externe demeure sous votre contrôle.",
            financial:
              "Les commissions officielles restent résolues par Giftshop. Aucun calcul financier n'est effectué ici.",
            gateway:
              "Forfy PRP est actif en mode coaching sécurisé. Aucune action financière ne peut être exécutée ici.",
            workspace:
              "Le cockpit PRP n'a pas pu charger. Actualisez votre session puis réessayez.",
          };


    useEffect(
      () => {
        if (!open) {
          return;
        }

        const previousOverflow =
          document.body.style.overflow;

        const previousHtmlOverflow =
          document.documentElement.style.overflow;

        document.body.style.overflow =
          "hidden";

        document.documentElement.style.overflow =
          "hidden";

        return () => {
          document.body.style.overflow =
            previousOverflow;

          document.documentElement.style.overflow =
            previousHtmlOverflow;
        };

        // data-lot0d7c-html-scroll-lock
      },
      [
        open,
      ],
    );

    // data-lot0d7b-body-scroll-lock

    // PRP_V6_EXTERNAL_FORFY_OPEN_EVENT
    useEffect(() => {
      const openForfy = () => setOpen(true);
      window.addEventListener(
        "forfeo:open-prp-forfy",
        openForfy,
      );

      return () => {
        window.removeEventListener(
          "forfeo:open-prp-forfy",
          openForfy,
        );
      };
    }, []);


    const refresh =
      async () => {
        setLoading(true);
        setError(null);

        try {
          const workspace =
            await loadPrpWorkspace();

          setData(workspace);
        } catch (caught) {
          const message =
            caught instanceof Error
              ? caught.message
              : "prp_dashboard_read_failed";

          setError(
            /Edge Function returned a non-2xx status code/i.test(message)
              ? text.workspace
              : message,
          );
        } finally {
          setLoading(false);
        }
      };

    useEffect(
      () => {
        if (!open) {
          setError(null);
          return;
        }

        if (
          open
          && !data
          && !loading
          && !error
        ) {
          void refresh();
        }
      },
      [open],
    );

    const targetOptions =
      useMemo<TargetOption[]>(
        () => {
          const options:
            TargetOption[] = [
              {
                key:
                  "overview",
                type:
                  "overview",
                id: null,
                label:
                  language === "en"
                    ? "My complete portfolio"
                    : "Mon portefeuille complet",
              },
            ];

          for (
            const lead
            of data?.leads
            || []
          ) {
            if (!lead.id) {
              continue;
            }

            options.push({
              key:
                `lead:${lead.id}`,
              type:
                "lead",
              id:
                String(
                  lead.id,
                ),
              label:
                `${
                  language === "en"
                    ? "Prospect"
                    : "Prospect"
                } · ${
                  lead.business_name
                  || "—"
                }`,
            });
          }

          for (
            const merchant
            of data?.merchants
            || []
          ) {
            if (!merchant.id) {
              continue;
            }

            options.push({
              key:
                `business:${merchant.id}`,
              type:
                "business",
              id:
                String(
                  merchant.id,
                ),
              label:
                `${
                  language === "en"
                    ? "Merchant"
                    : "Marchand"
                } · ${
                  merchant.name
                  || "—"
                }`,
            });
          }

          return options;
        },
        [
          data,
          language,
        ],
      );

    const run =
      async () => {
        const target =
          targetOptions.find(
            (option) =>
              option.key
              === selectedTarget,
          )
          || targetOptions[0];

        if (!target) {
          return;
        }

        setRunning(true);
        setError(null);
        setResult(null);

        try {
          const response =
            await runPrpForfy({
              mode,
              target_type:
                target.type,
              target_id:
                target.id,
              objective,
              language,
            });

          setResult(
            response.result,
          );
        } catch (caught) {
          const message =
            caught instanceof Error
              ? caught.message
              : "forfy_prp_failed";

          setError(
            message.includes(
              "forfy_gateway_unconfigured",
            )
              ? text.gateway
              : message,
          );
        } finally {
          setRunning(false);
        }
      };

    return (
      <>
        <button
          type="button"
          onClick={() =>
            setOpen(true)
          }
          data-prp-forfy-launcher="true"
          aria-expanded={open}
          aria-controls="prp-forfy-cockpit-dialog"
          aria-label={
            text.button
          }
          className="
            fixed
            z-[70]
            max-w-[calc(100vw-2rem)]
            whitespace-nowrap
            rounded-full
            bottom-[calc(5rem+env(safe-area-inset-bottom))]
            right-[calc(1rem+env(safe-area-inset-right))]
            sm:bottom-6
            sm:right-6
            lg:bottom-8
            lg:right-8
            bg-indigo-700
            px-4
            py-3
            text-xs
            sm:px-5
            sm:text-sm
            font-black
            text-white
            shadow-2xl
            transition
            hover:bg-indigo-800
            focus:outline-none
            focus:ring-4
            focus:ring-indigo-300
          "
        >
          🤖 {text.button}
        </button>

        {open && (
          <div
            data-lot0d7c-overlay-above-header="true"
            data-prp-forfy-overlay="true"
              className="
                fixed
                inset-0
                flex
                justify-center
                bg-slate-950/50
                backdrop-blur-sm
                sm:items-center
                sm:p-6
                z-[9999]
                items-stretch
                p-0
              "
            role="presentation"
          >
            <section
              id="prp-forfy-cockpit-dialog"
              data-prp-forfy-dialog="true"
              role="dialog"
              aria-modal="true"
              aria-labelledby="prp-forfy-title"
              data-lot0d7b-forfy-mobile-safe="true"
              data-lot0d7c-forfy-mobile-fullscreen="true"
              className="
                flex
                w-full
                max-w-4xl
                flex-col
                overflow-hidden
                bg-white
                shadow-2xl
                sm:max-h-[90dvh]
                sm:rounded-3xl
                h-[100dvh]
                max-h-[100dvh]
                rounded-none
                sm:h-auto
              "
            >
              <header
                className="
                flex
                items-start
                justify-between
                gap-4
                border-b
                border-slate-200
                bg-white
                sticky
                top-0
                p-4
                sm:p-7
                z-[10001]
              "
              >
                <div className="prp-forfy-heading">
                  <div className="prp-forfy-robot-avatar" aria-hidden="true">🤖</div>
                  <p
                    className="
                      mb-1
                      text-xs
                      font-black
                      uppercase
                      tracking-[0.18em]
                      text-indigo-600
                    "
                  >
                    FORFEO INC.
                  </p>

                  <h2
                    id="prp-forfy-title"
                    className="
                      text-xl
                      sm:text-2xl
                      font-black
                      text-slate-950
                    "
                  >
                    {text.title}
                  </h2>

                  <p
                    className="
                      mt-1
                      text-sm
                      text-slate-500
                    "
                  >
                    {text.subtitle}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setOpen(false)
                  }
                  data-lot0d7c-close-visible="true"
              className="
                shrink-0
                rounded-full
                border
                border-slate-200
                bg-white
                px-4
                py-2
                text-sm
                font-black
                text-slate-600
                hover:bg-slate-50
                z-[10002]
              "
                >
                  {text.close}
                </button>
              </header>

              <div
                className="
                  min-h-0
                  overscroll-contain
                  overflow-y-auto
                  pb-[calc(1rem+env(safe-area-inset-bottom))]
                  p-5
                  sm:p-7
                "
              >
                {loading && (
                  <div
                    role="status"
                    className="
                      rounded-2xl
                      bg-slate-50
                      p-5
                      font-bold
                      text-slate-600
                    "
                  >
                    …
                  </div>
                )}

                {error && (
                  <div
                    role="alert"
                    className="
                      mb-5
                      rounded-2xl
                      border
                      border-rose-200
                      bg-rose-50
                      p-4
                      text-sm
                      font-bold
                      text-rose-800
                    "
                  >
                    {error}
                  </div>
                )}

                {data && (
                  <>
                    <div
                      className="
                        mb-6
                        grid
                        grid-cols-2
                        gap-3
                        lg:grid-cols-4
                      "
                    >
                      {[
                        [
                          text.leads,
                          data.summary
                            .lead_count,
                        ],
                        [
                          text.merchants,
                          data.summary
                            .merchant_count,
                        ],
                        [
                          text.active,
                          data.summary
                            .approved_merchant_count,
                        ],
                        [
                          text.payouts,
                          data.summary
                            .pending_payout_count,
                        ],
                      ].map(
                        ([
                          label,
                          value,
                        ]) => (
                          <div
                            key={
                              String(
                                label,
                              )
                            }
                            className="
                              rounded-2xl
                              border
                              border-slate-200
                              bg-slate-50
                              p-4
                            "
                          >
                            <p
                              className="
                                text-xs
                                font-bold
                                text-slate-500
                              "
                            >
                              {label}
                            </p>

                            <p
                              className="
                                mt-2
                                text-3xl
                                font-black
                                text-slate-950
                              "
                            >
                              {number(
                                value,
                              )}
                            </p>
                          </div>
                        ),
                      )}
                    </div>

                    <div
                      className="
                        mb-6
                        rounded-2xl
                        border
                        border-indigo-100
                        bg-indigo-50
                        p-4
                        text-sm
                        text-indigo-950
                      "
                    >
                      <strong>
                        {data.profile
                          .full_name
                          || "PRP"}
                      </strong>

                      {" · "}

                      {data.profile
                        .region
                        || "—"}

                      <p
                        className="
                          mt-2
                          text-xs
                          font-semibold
                          text-indigo-800
                        "
                      >
                        {text.financial}
                      </p>
                    </div>

                    <div
                      className="
                        grid
                        gap-4
                        lg:grid-cols-2
                      "
                    >
                      <label
                        className="
                          text-sm
                          font-bold
                          text-slate-700
                        "
                      >
                        {text.target}

                        <select
                          value={
                            selectedTarget
                          }
                          onChange={
                            (event) =>
                              setSelectedTarget(
                                event
                                  .target
                                  .value,
                              )
                          }
                          className="
                            mt-2
                            w-full
                            rounded-xl
                            border
                            border-slate-300
                            bg-white
                            p-3
                          "
                        >
                          {targetOptions
                            .map(
                              (
                                option,
                              ) => (
                                <option
                                  key={
                                    option.key
                                  }
                                  value={
                                    option.key
                                  }
                                >
                                  {
                                    option.label
                                  }
                                </option>
                              ),
                            )}
                        </select>
                      </label>

                      <label
                        className="
                          text-sm
                          font-bold
                          text-slate-700
                        "
                      >
                        {text.mode}

                        <select
                          value={mode}
                          onChange={
                            (event) =>
                              setMode(
                                modeLabels.find(
                                  (option) =>
                                    option.value
                                    === event.target.value,
                                )?.value
                                ?? "next_best_action",
                              )
                          }
                          className="
                            mt-2
                            w-full
                            rounded-xl
                            border
                            border-slate-300
                            bg-white
                            p-3
                          "
                        >
                          {modeLabels
                            .map(
                              (
                                option,
                              ) => (
                                <option
                                  key={
                                    option.value
                                  }
                                  value={
                                    option.value
                                  }
                                >
                                  {
                                    language
                                    === "en"
                                      ? option.en
                                      : option.fr
                                  }
                                </option>
                              ),
                            )}
                        </select>
                      </label>
                    </div>

                    <label
                      className="
                        mt-4
                        block
                        text-sm
                        font-bold
                        text-slate-700
                      "
                    >
                      {text.objective}

                      <textarea
                        value={
                          objective
                        }
                        onChange={
                          (event) =>
                            setObjective(
                              event
                                .target
                                .value
                                .slice(
                                  0,
                                  500,
                                ),
                            )
                        }
                        maxLength={500}
                        rows={4}
                        placeholder={
                          text.objectivePlaceholder
                        }
                        className="
                          mt-2
                          w-full
                          resize-y
                          rounded-xl
                          border
                          border-slate-300
                          p-3
                        "
                      />
                    </label>

                    <div
                      className="
                        mt-4
                        flex
                        flex-col
                        gap-3
                        sm:flex-row
                      "
                    >
                      <button
                        type="button"
                        onClick={() =>
                          void run()
                        }
                        disabled={
                          running
                          || loading
                        }
                        className="
                          rounded-xl
                          bg-indigo-700
                          px-5
                          py-3
                          font-black
                          text-white
                          hover:bg-indigo-800
                          disabled:cursor-not-allowed
                          disabled:opacity-50
                        "
                      >
                        {running
                          ? "…"
                          : `✨ ${text.run}`}
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          void refresh()
                        }
                        disabled={
                          loading
                        }
                        className="
                          rounded-xl
                          border
                          border-slate-300
                          px-5
                          py-3
                          font-black
                          text-slate-700
                          hover:bg-slate-50
                        "
                      >
                        {text.refresh}
                      </button>
                    </div>

                    <p
                      className="
                        mt-4
                        text-xs
                        font-semibold
                        text-slate-500
                      "
                    >
                      {text.human}
                    </p>

                    {result && (
                      <article
                        className="
                          mt-6
                          whitespace-pre-wrap
                          rounded-2xl
                          border
                          border-emerald-200
                          bg-emerald-50
                          p-5
                          text-sm
                          leading-6
                          text-slate-900
                        "
                      >
                        {result}
                      </article>
                    )}
                  </>
                )}

                {!loading
                  && !data
                  && !error && (
                    <p
                      className="
                        text-sm
                        text-slate-500
                      "
                    >
                      {text.noData}
                    </p>
                  )}
              </div>
            </section>
          </div>
        )}
      </>
    );
  };

export default PrpGrowthCockpit;
